import { useChatActions, useChatMessages, useChatStatus } from '@ai-sdk-tools/store';
import { browserLogger } from '@newfront-insurance/dd-rum';
import type { ChatOnFinishCallback } from 'ai';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { isNovaScheduleProposalToolPart } from '../../../lib/nova-schedule-proposal';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { DurablePreviewProvider, useDurablePreviewState } from '../contexts/durable-preview-context';
import { useNovaChat } from '../hooks/use-nova-chat';
import { getChatErrorPresentation } from '../lib/chat-error-presentation';
import { settleNovaTurnTelemetry, settleNovaTurnTelemetryForViewChange } from '../lib/nova-turn-telemetry';
import { settleToolHandoffContinuation } from '../lib/tool-handoff-continuation';
import { isDurableNovaChatCompletionsEndpoint } from '../lib/transport';
import type { UIMessageWithMetadata } from '../lib/types';

interface ChatRootProps {
  children: ReactNode;
}

/**
 * Root component that sets up the chat context using @ai-sdk-tools/store.
 * This component initializes the chat hook and makes chat state available
 * to all child components via context.
 *
 * Reads chatUuid, initialMessages, and callbacks from context.
 * Syncs hasAnyMessages from AI SDK store to ChatOverlayContext so NovaProvider
 * can use it without needing access to the AI SDK Provider.
 *
 * Stream lifecycle callbacks refresh the server-backed chats list. Schedule
 * proposals also rehydrate their finalized server preview before confirmation;
 * the active-chat guard keeps a late refresh from replacing another chat.
 *
 * @example
 * ```tsx
 * <ChatRoot>
 *   <ChatHeader>...</ChatHeader>
 *   <Messages />
 *   <ChatInput />
 * </ChatRoot>
 * ```
 */
export function ChatRoot({ children }: ChatRootProps) {
  const {
    chatUuid,
    initialMessages,
    callbacks,
    setHasAnyMessages,
    setActiveChat,
    notificationController,
    apiEndpoint,
    setChatError,
    chatActiveStreamId,
    isActiveChat,
    isLoadingMessages,
    refetchMessages,
    canResumeActiveStream,
    setRetryLastRequest,
  } = useChatOverlayContext();
  const messages = useChatMessages<UIMessageWithMetadata>();
  const status = useChatStatus();
  const { setMessages } = useChatActions<UIMessageWithMetadata>();
  const previousStatusRef = useRef(status);
  const preservedStopPreviewChatUuidRef = useRef<string | undefined>(undefined);
  const telemetryChatUuidRef = useRef(chatUuid);
  const resumeStreamRef = useRef<() => void>(() => undefined);
  const durablePreview = useDurablePreviewState(chatUuid);
  const { clear: clearDurablePreview, onData: handleDurablePreviewData } = durablePreview;
  const [schedulePreviewHydratingChatUuid, setSchedulePreviewHydratingChatUuid] = useState<string>();

  if (
    schedulePreviewHydratingChatUuid !== chatUuid &&
    (status === 'submitted' || status === 'streaming') &&
    messages.some(scheduleProposalNeedsServerPreview)
  ) {
    setSchedulePreviewHydratingChatUuid(chatUuid);
  }

  const handleFinish: ChatOnFinishCallback<UIMessageWithMetadata> = useCallback(
    async (event) => {
      settleToolHandoffContinuation(chatUuid, event);

      // A stopped reconnect still reaches AI SDK's onFinish cleanup. It does
      // not represent a completed turn and may belong to a replaced chat view.
      if (event.isAbort) {
        setSchedulePreviewHydratingChatUuid((current) => (current === chatUuid ? undefined : current));
        return;
      }

      if (event.isError) {
        settleNovaTurnTelemetry(chatUuid, 'error');
      }

      if (!event.isError) {
        setChatError(undefined);
        setRetryLastRequest(undefined);

        if (scheduleProposalNeedsServerPreview(event.message)) {
          setSchedulePreviewHydratingChatUuid(chatUuid);
          try {
            const canonicalMessages = await refetchMessages(chatUuid);
            if (canonicalMessages && isActiveChat(chatUuid)) {
              setMessages(canonicalMessages);
            }
          } catch (error) {
            browserLogger.debug('[ChatRoot] Schedule proposal preview refetch failed', { chatUuid, error });
          } finally {
            setSchedulePreviewHydratingChatUuid((current) => (current === chatUuid ? undefined : current));
          }
        }
      }

      // A failed first request may never have created the chat. Refresh server
      // state without marking a nonexistent chat as read.
      if (event.isError) {
        notificationController.refetchChats();
      } else {
        notificationController.noteStreamFinish(chatUuid);
      }

      callbacks?.onFinish?.();
    },
    [
      callbacks,
      chatUuid,
      isActiveChat,
      notificationController,
      refetchMessages,
      setChatError,
      setMessages,
      setRetryLastRequest,
    ],
  );

  // Handle stream errors by surfacing them to the UI via overlay context
  const handleError = useCallback(
    (error: Error) => {
      // User stops, chat replacement, unmount, and an initial 204 reconnect all
      // intentionally abort the response. None should surface as a chat error.
      if (error.name === 'AbortError') {
        return;
      }

      settleNovaTurnTelemetry(chatUuid, 'error');

      browserLogger.error('[ChatRoot] Stream error', {
        chatUuid,
        errorMessage: error.message,
        errorName: error.name,
        stack: error.stack,
      });

      const presentation = getChatErrorPresentation(error);

      setChatError({
        ...presentation,
        timestamp: new Date(),
        canRetry: true,
      });

      if (presentation.type === 'network') {
        setRetryLastRequest(() => resumeStreamRef.current());
      }

      callbacks?.onError?.(error);
      setSchedulePreviewHydratingChatUuid((current) => (current === chatUuid ? undefined : current));
    },
    [callbacks, chatUuid, setChatError, setRetryLastRequest],
  );

  const handleReconnectUnavailable = useCallback(
    async (reconnectedChatUuid: string) => {
      // The server is the authority on whether a stream still exists. Replace
      // stale local state with the persisted snapshot after a disconnect.
      notificationController.refetchChats();
      try {
        const canonicalMessages = await refetchMessages(reconnectedChatUuid);
        if (!canonicalMessages) {
          return;
        }
        if (chatUuid.startsWith('new-chat-')) {
          setActiveChat(reconnectedChatUuid);
        }
        setChatError(undefined);
        setRetryLastRequest(undefined);
      } catch (error) {
        browserLogger.debug('[ChatRoot] Current chat refetch failed after reconnect', { chatUuid, error });
      }
    },
    [chatUuid, notificationController, refetchMessages, setActiveChat, setChatError, setRetryLastRequest],
  );

  const handleDurableStop = useCallback(
    async (stoppedChatUuid: string) => {
      setChatError(undefined);
      setRetryLastRequest(undefined);

      // The stop endpoint returns only after checkpointed partial output is persisted. Replace
      // the local streaming copy with that canonical message so the current view matches reload.
      try {
        const canonicalMessages = await refetchMessages(stoppedChatUuid);
        if (canonicalMessages && preservedStopPreviewChatUuidRef.current === stoppedChatUuid) {
          preservedStopPreviewChatUuidRef.current = undefined;
          if (isActiveChat(stoppedChatUuid)) {
            setMessages(canonicalMessages);
            clearDurablePreview();
          }
        }
      } catch (error) {
        browserLogger.debug('[ChatRoot] Current chat refetch failed after durable stop', {
          chatUuid: stoppedChatUuid,
          error,
        });
      } finally {
        notificationController.noteStreamFinish(stoppedChatUuid);
      }
    },
    [
      clearDurablePreview,
      isActiveChat,
      notificationController,
      refetchMessages,
      setChatError,
      setMessages,
      setRetryLastRequest,
    ],
  );

  const handleDurableStopStart = useCallback((stoppedChatUuid: string) => {
    // stop() makes the chat ready synchronously. Mark this first so the last display-only
    // preview remains visible until the persisted partial message replaces it.
    preservedStopPreviewChatUuidRef.current = stoppedChatUuid;
  }, []);

  const handleDurableStopError = useCallback(
    (error: Error) => {
      browserLogger.error('[ChatRoot] Durable stop failed', {
        chatUuid,
        errorMessage: error.message,
        errorName: error.name,
        stack: error.stack,
      });
      setChatError({
        ...getChatErrorPresentation(error),
        timestamp: new Date(),
        canRetry: false,
      });
      setRetryLastRequest(undefined);

      if (preservedStopPreviewChatUuidRef.current === chatUuid) {
        preservedStopPreviewChatUuidRef.current = undefined;
        if (isActiveChat(chatUuid)) {
          clearDurablePreview();
        }
      }
    },
    [chatUuid, clearDurablePreview, isActiveChat, setChatError, setRetryLastRequest],
  );

  // Initialize chat hook - this makes chat state available via context.
  // Resume is held back until the server snapshot has hydrated so a
  // reconnected stream always appends after the restored history.
  const chat = useNovaChat({
    chatUuid,
    initialMessages,
    activeStreamId: chatActiveStreamId,
    canResume: !isLoadingMessages && canResumeActiveStream,
    onDurableActiveStreamReconciled: handleReconnectUnavailable,
    onDurableStop: handleDurableStop,
    onDurableStopError: handleDurableStopError,
    onDurableStopStart: handleDurableStopStart,
    onReconnectUnavailable: handleReconnectUnavailable,
    onDurablePreview: handleDurablePreviewData,
    onFinish: handleFinish,
    onError: handleError,
  });
  const { resumeStream } = chat;
  useEffect(() => {
    resumeStreamRef.current = () => {
      if (chatActiveStreamId && isDurableNovaChatCompletionsEndpoint(apiEndpoint)) {
        void resumeStream({ body: { workflowRunId: chatActiveStreamId, startIndex: 0 } });
        return;
      }

      void resumeStream();
    };
  }, [apiEndpoint, chatActiveStreamId, resumeStream]);

  useEffect(() => {
    setChatError(undefined);
    setRetryLastRequest(undefined);
  }, [chatUuid, setChatError, setRetryLastRequest]);

  useEffect(() => {
    settleNovaTurnTelemetryForViewChange(telemetryChatUuidRef.current, chatUuid);
    telemetryChatUuidRef.current = chatUuid;
  }, [chatUuid]);

  useEffect(
    () => () => {
      settleNovaTurnTelemetry(telemetryChatUuidRef.current, 'view_replaced');
    },
    [],
  );

  // Sync hasAnyMessages from AI SDK store to ChatOverlayContext
  // This ensures NovaProvider always has the current message count
  useEffect(() => {
    setHasAnyMessages(messages.length > 0);
  }, [messages.length, setHasAnyMessages]);

  // Clear any lingering error banner when a new request starts.
  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    const wasActive = previousStatus === 'submitted' || previousStatus === 'streaming';
    const isActive = status === 'submitted' || status === 'streaming';

    if (!wasActive && isActive) {
      setChatError(undefined);
      preservedStopPreviewChatUuidRef.current = undefined;
      clearDurablePreview();
    }

    // Commit events normally remove the speculative lane before canonical promotion.
    // Preserve it only while an optimistic durable Stop is awaiting its canonical snapshot.
    if (wasActive && !isActive && preservedStopPreviewChatUuidRef.current !== chatUuid) {
      clearDurablePreview();
    }

    previousStatusRef.current = status;
  }, [chatUuid, clearDurablePreview, setChatError, status]);

  return (
    <DurablePreviewProvider
      value={durablePreview}
      isSchedulePreviewHydrating={schedulePreviewHydratingChatUuid === chatUuid}
    >
      {children}
    </DurablePreviewProvider>
  );
}

function scheduleProposalNeedsServerPreview(message: UIMessageWithMetadata): boolean {
  return message.parts.some(
    (part) =>
      isNovaScheduleProposalToolPart(part) && part.state === 'input-available' && !part.input.preview?.firstRunAt,
  );
}
